import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, resolveAudioUrl, TUTTI_VOICE, PERFORMANCE_VOICE } from "./api";
import { useAuth } from "../auth";

// One app-wide audio engine: a queue of songs, each resolved to the member's SATB part at play time.
const PlayerCtx = createContext(null);
export const usePlayer = () => useContext(PlayerCtx);
export const AUDIO_EXCLUSIVE = "ulc-audio-play";
// Repeat modes (song and list): normal, one extra time, continuous.
export const REPEAT_MODES = ["off", "once", "loop"];

export function pickPart(song, voice) {
  const parts = (song?.parts || []).filter((p) => p.audio_url);
  const mine = voice && parts.find((p) => p.voice === voice);
  if (mine) return { part: mine, fallback: false };
  const fb = parts.find((p) => p.voice === TUTTI_VOICE) || parts.find((p) => p.voice === PERFORMANCE_VOICE) || parts[0];
  return { part: fb || null, fallback: !!fb };
}

const shuffled = (n, first) => {
  const rest = [...Array(n).keys()].filter((i) => i !== first);
  for (let i = rest.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [rest[i], rest[j]] = [rest[j], rest[i]]; }
  return first == null || first < 0 ? rest : [first, ...rest];
};
const nextMode = (m) => REPEAT_MODES[(REPEAT_MODES.indexOf(m) + 1) % REPEAT_MODES.length];

export function PlayerProvider({ children }) {
  const { user } = useAuth();
  const audioRef = useRef(null);
  if (!audioRef.current && typeof Audio !== "undefined") { audioRef.current = new Audio(); audioRef.current.preload = "auto"; }
  const [queue, setQueue] = useState([]);
  const [order, setOrder] = useState([]);
  const [pos, setPos] = useState(-1);
  const [nonce, setNonce] = useState(0);
  const [repeatSong, setRepeatSong] = useState("off");
  const [repeatList, setRepeatList] = useState("off");
  const [shuffle, setShuffle] = useState(false);
  const [context, setContext] = useState(null);
  const [now, setNow] = useState(null);
  const [st, setSt] = useState({ playing: false, t: 0, d: 0, loading: false });
  const loadId = useRef(0);
  const songLeft = useRef(1);
  const listLeft = useRef(1);
  const advanceRef = useRef(() => {});

  const index = pos >= 0 ? order[pos] : -1;
  const item = index >= 0 ? queue[index] : null;
  const itemRef = useRef(item);
  itemRef.current = item;
  const nextItem = pos >= 0 && pos + 1 < order.length ? queue[order[pos + 1]] : (pos >= 0 && repeatList !== "off" && order.length > 1 ? queue[order[0]] : null);

  const go = useCallback((p) => { setPos(p); setNonce((n) => n + 1); }, []);

  useEffect(() => {
    const a = audioRef.current;
    const upd = (x = {}) => setSt((s) => ({ ...s, playing: !a.paused, t: a.currentTime || 0, d: a.duration || 0, ...x }));
    const onPlay = () => { upd(); window.dispatchEvent(new CustomEvent(AUDIO_EXCLUSIVE, { detail: a })); };
    const onOther = (e) => { if (e.detail !== a) a.pause(); };
    const evs = { timeupdate: () => upd(), pause: () => upd(), play: onPlay, waiting: () => upd({ loading: true }), playing: () => upd({ loading: false }), loadedmetadata: () => upd({ loading: false }), ended: () => advanceRef.current(true) };
    Object.entries(evs).forEach(([k, f]) => a.addEventListener(k, f));
    window.addEventListener(AUDIO_EXCLUSIVE, onOther);
    return () => { Object.entries(evs).forEach(([k, f]) => a.removeEventListener(k, f)); window.removeEventListener(AUDIO_EXCLUSIVE, onOther); };
  }, []);

  // Load only when the user moves to another track (nonce) — reordering the queue never restarts the current song.
  useEffect(() => {
    const a = audioRef.current;
    const it = itemRef.current;
    if (!it) { a.pause(); setNow(null); return; }
    songLeft.current = 1;
    const id = ++loadId.current;
    setSt((s) => ({ ...s, loading: true, t: 0 }));
    api(`/songs/${it.song_id}`)
      .then((song) => {
        if (id !== loadId.current) return;
        const want = it.voice || user?.voice;
        const { part, fallback } = pickPart(song, want);
        setNow({ song, part, fallback, wanted: want });
        if (!part) { setSt((s) => ({ ...s, loading: false })); return; }
        a.src = resolveAudioUrl(part.audio_url);
        a.play().catch((err) => console.error("Reprodução bloqueada", err));
      })
      .catch((err) => { if (id === loadId.current) { setNow({ song: it, part: null, error: err.message }); setSt((s) => ({ ...s, loading: false })); } });
  }, [nonce, user?.voice]);

  const restart = () => { const a = audioRef.current; a.currentTime = 0; a.play().catch(() => {}); };

  const advance = useCallback((auto = false) => {
    const a = audioRef.current;
    if (auto && repeatSong === "loop") { restart(); return; }
    if (auto && repeatSong === "once" && songLeft.current > 0) { songLeft.current -= 1; restart(); return; }
    if (pos + 1 < order.length) { go(pos + 1); return; }
    const again = repeatList === "loop" || (repeatList === "once" && listLeft.current > 0);
    if (again && order.length) {
      if (repeatList === "once") listLeft.current -= 1;
      if (shuffle) setOrder(shuffled(queue.length, -1));
      go(0);
      return;
    }
    if (auto) a.pause();
  }, [pos, order.length, repeatSong, repeatList, shuffle, queue.length, go]);
  advanceRef.current = advance;

  const playQueue = useCallback((items, start = 0, ctx = null) => {
    const list = items.filter((x) => x?.song_id);
    if (!list.length) return;
    setQueue(list);
    setContext(ctx);
    listLeft.current = 1;
    const ord = shuffle ? shuffled(list.length, start) : [...list.keys()];
    setOrder(ord);
    go(shuffle ? 0 : start);
  }, [shuffle, go]);

  // Personal playlists: apply a new order/content immediately, keeping the current song playing.
  const live = useRef({});
  live.current = { context, shuffle, pos };
  const syncQueue = useCallback((key, items) => {
    const { context, shuffle, pos } = live.current;
    if (!context?.key || context.key !== key) return;
    const list = items.filter((x) => x?.song_id);
    if (!list.length) return;
    const cur = itemRef.current?.song_id;
    const at = list.findIndex((x) => x.song_id === cur);
    setQueue(list);
    if (shuffle) { setOrder(shuffled(list.length, at)); setPos(at >= 0 ? 0 : -1); }
    else { setOrder([...list.keys()]); setPos(at >= 0 ? at : Math.min(Math.max(pos, 0), list.length - 1)); }
    if (at < 0) setNonce((n) => n + 1);
  }, []);

  const toggleShuffle = () => {
    const next = !shuffle;
    setShuffle(next);
    if (queue.length) { setOrder(next ? shuffled(queue.length, index) : [...queue.keys()]); setPos(next ? 0 : index); }
  };

  useEffect(() => { if (!user) { audioRef.current.pause(); setQueue([]); setOrder([]); setPos(-1); } }, [user]);

  const value = {
    queue, order, pos, index, item, nextItem, now, status: st, repeatSong, repeatList, shuffle, context,
    playQueue, syncQueue,
    jumpTo: (qi) => go(order.indexOf(qi)),
    toggle: () => { const a = audioRef.current; if (a.paused) a.play().catch(() => {}); else a.pause(); },
    next: () => advance(false),
    prev: () => { const a = audioRef.current; if (a.currentTime > 3 || pos <= 0) a.currentTime = 0; else go(pos - 1); },
    seek: (sec) => { audioRef.current.currentTime = sec; },
    cycleRepeatSong: () => { songLeft.current = 1; setRepeatSong(nextMode); },
    cycleRepeatList: () => { listLeft.current = 1; setRepeatList(nextMode); },
    toggleShuffle,
    close: () => { audioRef.current.pause(); setQueue([]); setOrder([]); setPos(-1); setContext(null); },
  };
  return <PlayerCtx.Provider value={value}>{children}</PlayerCtx.Provider>;
}

/** Queue items for an official event setlist, preserving the admin's order. */
export const eventQueue = (event) => (event?.songs || []).map((s) => ({ song_id: s.id, title: s.title, artist: s.artist, cover_url: s.cover_url }));
export const songsQueue = (songs) => songs.map((s) => ({ song_id: s.id, title: s.title, artist: s.artist, cover_url: s.cover_url }));
